import * as Sentry from '@sentry/browser';
import type { ColumnRowGroupChangedEvent } from 'ag-grid-community';
import type { UserRole, UserScopesT, UserT } from 'business/definitions';
import {
    IS_DEV,
    IS_E2E,
    IS_PREPROD,
    IS_PRESENTATION,
    IS_PROD,
    IS_STAGING,
} from 'env';
import { useSafeState } from 'helpers/hooks';
import { isAdminBudget } from 'hooks/user';
import _, { compact, debounce } from 'lodash';
import type { IntegrationT } from 'routes/IntegrationsList/types';
import {
    CSV,
    type DecisionPerimeterT,
} from 'routes/Support/components/ClientSupport/components/ClientConfiguration/ModuleForm/helpers/definitions';
import { getClientBind } from 'store/APIClient';

import {
    ACCESS_BUDGETS_GESTION,
    ACCESS_CADRAGE_GESTION,
    ACCESS_DAF,
    ACCESS_PHASES_GESTION,
    ADMIN,
    ADMIN_CATEGORIES,
    AI_ASSISTANT_USER,
    BUDGET,
    BUDGET_USER,
    DECISION,
    DECISION_USER,
    EXPLORE_USER,
    HUMAN_RESOURCES,
    HUMAN_RESOURCES_USER,
    RAWDATA,
    SUPER_FINANCES_WRITER,
} from '../business/constants';
import type { ModuleT } from '../business/types';

export const ACTIONS = Object.freeze({
    group: 'groupement',
    ungroup: 'dégroupement',
    open: 'ouverture',
    close: 'fermeture',
    hide: 'cacher',
    show: 'afficher',
});
type Actions = typeof ACTIONS;
type ActionsValues = Actions[keyof Actions];

const bind = getClientBind();

const pendoTrack = (eventName: string, metadata?: pendo.Metadata) => {
    if (!window.pendo) {
        console.error(
            `Trying to track ${eventName}, but Pendo is not available, so ignoring it`,
        );
        return;
    }
    window.pendo.track(eventName, metadata);
};

export function goToGuideNextStep() {
    const result = window.pendo?.getActiveGuide();

    if (result) {
        const { step } = result;
        step.advance();
    }
}

export function trackEvent(eventName: string, metadata?: pendo.Metadata) {
    if ((IS_PROD || IS_STAGING || IS_PRESENTATION || IS_PREPROD) && !IS_E2E) {
        pendoTrack(eventName, metadata);
    }
}

export function trackEventModule(
    eventName: string,
    module: string,
    metadata?: pendo.Metadata,
) {
    trackEvent(eventName, { ...metadata, module });
}

export function trackEventDecision(
    eventName: string,
    metadata?: pendo.Metadata,
) {
    trackEvent(eventName, { ...metadata, module: 'Décision' });
}

export function trackEventBudget(eventName: string, metadata?: pendo.Metadata) {
    trackEvent(eventName, { ...metadata, module: 'Budget' });
}

export function trackEventPRH(eventName: string, metadata?: pendo.Metadata) {
    if ((IS_DEV || IS_STAGING) && !IS_E2E) {
        // eslint-disable-next-line no-console
        console.log(eventName, metadata);
    }
    trackEvent(eventName, { ...metadata, module: 'Prospective RH' });
}

function mapUserRolesToPendo(roles: UserRole[]) {
    return {
        admin: roles.includes(ADMIN),
        adminBudget: isAdminBudget(roles),
        budget: roles.includes(BUDGET_USER),
        decision: roles.includes(DECISION_USER),
        exploreAccess: roles.includes(EXPLORE_USER),
        aiAssistantAccess: roles.includes(AI_ASSISTANT_USER),
        humanResources: roles.includes(HUMAN_RESOURCES_USER),
        rawDataAccess: roles.includes(RAWDATA),
        accessDaf: roles.includes(ACCESS_DAF),
        accessBudgetsGestion: roles.includes(ACCESS_BUDGETS_GESTION),
        accessCadrageGestion: roles.includes(ACCESS_CADRAGE_GESTION),
        accessPhasesGestion: roles.includes(ACCESS_PHASES_GESTION),
        adminCategories: roles.includes(ADMIN_CATEGORIES),
        superFinancesWriter: roles.includes(SUPER_FINANCES_WRITER),
    };
}

function mapUserScopesToPendo(scopes: UserScopesT) {
    const getAccessType = (scopeName: string) =>
        scopes.find((s) => s.name === scopeName)?.accessType ?? null;

    return {
        defaultFinance: getAccessType('default_finance'),
        defaultHr: getAccessType('default_hr'),
        personalHr: getAccessType('personal_hr'),
        defaultPopulation: getAccessType('default_population'),
        defaultTechnical: getAccessType('default_technical'),
    };
}

function mapModulesToPendo(modules: ModuleT[]) {
    return {
        hasBudget: modules?.includes(BUDGET),
        hasDecision: modules?.includes(DECISION),
        hasProspectiveRH: modules?.includes(HUMAN_RESOURCES),
    };
}

/**
 * Update the pendo account with the modules.
 * We have to update them separately, otherwise they may be initialized before the user has all the modules.
 */
export function updatePendoModules(modules: ModuleT[]) {
    if ('pendo' in window && modules.length > 0) {
        window.pendo.updateOptions({
            account: mapModulesToPendo(modules),
        });
    }
}

/**
 * Update the pendo account with the decision perimeters (only focuses on CSV for the moment).
 */
export function updatePendoDecisionPerimeters(
    decisionPerimeters: DecisionPerimeterT[],
) {
    if ('pendo' in window && decisionPerimeters?.length > 0) {
        window.pendo.updateOptions({
            account: { hasCsv: decisionPerimeters.includes(CSV) },
        });
    }
}

export function updatePendoSoftwares(integrations: IntegrationT[] | undefined) {
    if ('pendo' in window && integrations) {
        window.pendo.updateOptions({
            account: {
                softwares: integrations
                    .filter((i) => i.isActive)
                    .map((i) => i.software),
            },
        });
    }
}

function initializePendo(
    user: UserT,
    isEssential: boolean,
    isStagingEnv: boolean,
) {
    const visitor = {
        id: `${bind}-${user.email}`,
        email: user.email,
        bind,
    };
    const account = {
        id: bind,
        bind,
    };
    if (isStagingEnv) {
        Object.keys(visitor).forEach(
            (key) => (visitor[key] = `STAGING-${visitor[key]}`),
        );
        Object.keys(account).forEach(
            (key) => (account[key] = `STAGING-${account[key]}`),
        );
    }
    window.pendo.initialize({
        visitor: {
            ...visitor,
            ...mapUserRolesToPendo(user.roles),
            ...mapUserScopesToPendo(user.scopes),
        },
        account: {
            ...account,
            isEssential,
        },
    });
}

/**
 * Tracks an anonymous user on Sentry and Pendo
 */
export function trackAnonymousUser() {
    const anonymUser = {
        id: 'anonymous',
        email: 'anonymous',
        bind,
    };

    Sentry.setUser({
        email: anonymUser.email,
        id: `${bind}_${anonymUser.id}`,
        bind,
    });
    if ('pendo' in window) {
        if (IS_PROD || IS_PRESENTATION || IS_PREPROD) {
            window.pendo.initialize({
                visitor: {
                    id: `${bind}-${anonymUser.email}`,
                    email: anonymUser.email,
                    // custom info:
                    bind,
                },
                account: {
                    id: bind,
                    // custom info:
                    bind,
                },
            });
        }
        if (IS_STAGING) {
            window.pendo.initialize({
                visitor: {
                    id: `STAGING-${bind}-${anonymUser.email}`,
                    email: `STAGING-${anonymUser.email}`,
                    // custom info:
                    bind: `STAGING-${bind}`,
                },
                account: {
                    id: `STAGING-${bind}`,
                    // custom info:
                    bind: `STAGING-${bind}`,
                },
            });
        }
    }
}

export function trackUser(user: UserT, isEssential: boolean) {
    if (IS_PROD && window._hsq && window.HubSpotConversations) {
        window._hsq.push([
            'identify',
            {
                email: user.email,
                ...(user.firstname && { firstname: user.firstname }),
                ...(user.lastname && { lastname: user.lastname }),
                ...(user.job && { jobtitle: user.job }),
                ...(user.gender && {
                    salutation: user.gender === 'M' ? 'Monsieur' : 'Madame',
                }),
            },
        ]);
        window._hsq.push(['trackPageView']);
        window.HubSpotConversations.widget.refresh();
    }

    Sentry.setUser({
        email: user.email,
        id: `${bind}_${user.id}`,
        bind,
    });

    // ads blocker can block pendo, we check if it is available
    if ('pendo' in window) {
        if (IS_PROD || IS_PRESENTATION || IS_STAGING || IS_PREPROD) {
            initializePendo(user, isEssential, IS_STAGING);
        }
    }
}

export const useGetTrackAgGridGrouping = (
    eventName: string,
    metadata?: pendo.Metadata,
): ((arg1: string[]) => void) => {
    const [groupColumns, setGroupColumns] = useSafeState<string[]>(
        [],
        (o, n) => _.intersection(o, n).length === _.union(o, n).length,
    );

    return (newGroupCols) => {
        let action: ActionsValues | undefined = undefined;
        let column;
        if (newGroupCols.length > groupColumns.length) {
            action = ACTIONS.group;
            column = newGroupCols.find((c) => !groupColumns.includes(c));
        } else if (newGroupCols.length < groupColumns.length) {
            action = ACTIONS.ungroup;
            column = groupColumns.find((c) => !newGroupCols.includes(c));
        }
        if (column && action) {
            trackEventBudget(eventName, { ...metadata, action, column });
        }
        setGroupColumns(newGroupCols);
    };
};

export type OnGroupTracking = (event: ColumnRowGroupChangedEvent) => void;
export const useGetAgGridTracking = (
    eventGroup: {
        gridGrouping: string;
        gridSearch: string;
        gridSort: string;
        [key: string]: unknown;
    },
    metadata?: pendo.Metadata,
): {
    onSearch: () => void;
    onSort: () => void;
    onGroup: OnGroupTracking;
} => {
    const trackGrouping = useGetTrackAgGridGrouping(
        eventGroup.gridGrouping,
        metadata,
    );
    return {
        onSearch: debounce(() => {
            trackEventBudget(eventGroup.gridSearch, metadata);
        }, 3000),
        onSort: () => {
            trackEventBudget(eventGroup.gridSort, metadata);
        },
        onGroup: (event: ColumnRowGroupChangedEvent) =>
            trackGrouping(
                compact(event.columns?.map((c) => c.getColDef().headerName)),
            ),
    };
};

export const TRACKABLE_EVENTS = {
    budget: {
        budgetOverview: {
            totals: {
                refreshData: 'budget/vuedaf/totaux/click-actualiser-donnees',
                refreshColumns: 'budget/vuedaf/totaux/click-actualiser-colonne',
                expandBlock: 'budget/vuedaf/totaux/depli-bloc',
                exportBudgetBalance:
                    'budget/vuedaf/totaux/export-grands-équilibres',
                exportRatio: 'budget/vuedaf/totaux/export-ratios',
            },
            analyse_arguments: {
                refreshData:
                    'budget/vuedaf/analyse-arguments/click-actualiser-donnees',
                export: 'budget/vuedaf/analyse-arguments/export',
                penModalOpened:
                    'budget//*/budget_overview/analyse_arguments/*/open-inscriptions-modal',
                pivotGridExport:
                    'budget//*/budget_overview/analyse_arguments/*/export',
                saveFilter:
                    'budget//*/budget_overview/analyse_arguments/*/edit-filter',
                openArgumentModal:
                    'budget//*/budget_overview/analyse_arguments/*/open-arguments-modal',
                closeArgumentModal:
                    'budget//*/budget_overview/analyse_arguments/*/close-arguments-modal',
                openArgument:
                    'budget//*/budget_overview/analyse_arguments/*/open-argument',
                sortArgument:
                    'budget//*/budget_overview/analyse_arguments/*/edit-arguments-modal-sort',
            },
            inscriptions: {
                expenseIncomeNewTab: 'budget/vuedaf/inscriptions/ouverture-DR',
                refreshData:
                    'budget/vuedaf/inscriptions/click-actualiser-donnees',
                export: 'budget/vuedaf/inscriptions/export',
                historyModal:
                    'budget//*/budget_overview/all_inscriptions/click-inscriptions-history',
            },
            operations: {
                unpackService: 'budget/vuedaf/opérations/depli-services',
                refreshData:
                    'budget/vuedaf/opérations/click-actualiser-donnees',
                export: 'budget/vuedaf/opérations/export',
                operationClicked: 'budget/vuedaf/operations/clic-opération',
                inscriptions: {
                    export: 'budget//*/budget_operation_view/export-inscriptions',
                    refreshData:
                        'budget//*/budget_operation_view/refresh-inscriptions',
                },
                draftOperation: {
                    create: 'budget/vuedaf/operations/creer-operation-manty',
                    confirmCreation:
                        'budget/vuedaf/operations/valider-creation-operation-manty',
                    toggleDetails:
                        'budget/vuedaf/operations/deplier-operation-manty',
                    delete: 'budget/vuedaf/operations/supprimer-operation-manty',
                    confirmDeletion:
                        'budget/vuedaf/operations/valider-supprimer-operation-manty',
                    rename: 'budget/vuedaf/operations/renommer-operation-manty',
                    confirmRename:
                        'budget/vuedaf/operations/valider-renommer-operation-manty',
                    editAssociations:
                        'budget/vuedaf/operations/acces-operation-manty',
                    confirmEditAssociations:
                        'budget/vuedaf/operations/valider-acces-operation-manty',
                    associate:
                        'budget/budgetproposal/operation/ajout-operation-manty-a-inscription',
                },
            },
            aps: {
                pageOpened: 'budget/vuedaf/aps/consultation-page',
                unpackOperations: 'budget/vuedaf/aps/depli-operations',
                refreshData: 'budget/vuedaf/aps/click-actualiser-donnees',
                export: 'nom : budget/vuedaf/aps/export',
            },
            projections: {
                atterrissage: {
                    pageOpened:
                        'budget/vuedaf/projections/atterrissage/consultation-page',
                    amountUpdated:
                        'budget/vuedaf/projections/atterrissage/montant-modif',
                    detailsClicked:
                        'budget/vuedaf/projections/atterrissage/clic-detail',
                    commentUpdated:
                        'budget/vuedaf/projections/atterrissage/justif-modif',
                    statusUpdated:
                        'budget/vuedaf/projections/atterrissage/statut-modif',
                    filtered: 'budget/vuedaf/projections/atterrissage/filtre',
                    sectionToggled:
                        'budget/vuedaf/projections/atterrissage/toggle-section',
                    resourceToggled:
                        'budget/vuedaf/projections/atterrissage/toggle-ressource',
                    groupingChanged:
                        'budget/vuedaf/projections/atterrissage/modif-ordre-groupement',
                    export: 'budget/vuedaf/projections/atterrissage/export',
                    compactModeToggled:
                        'budget/vuedaf/projections/atterrissage/compact-mode',
                },
                pluriannual: {
                    pageOpened:
                        'budget/vuedaf/projections/pluriannuelles/consultation-page',
                    filtered:
                        'budget/vuedaf/projections/pluriannuelles/modif-filtre',
                    sectionToggled:
                        'budget/vuedaf/projections/pluriannuelles/toggle-section',
                    resourceToggled:
                        'budget/vuedaf/projections/pluriannuelles/toggle-ressource',
                    amountUpdated:
                        'budget/vuedaf/projections/pluriannuelles/montant-modif',
                    detailsClicked:
                        'budget/vuedaf/projections/pluriannuelles/clic-detail',
                    commentUpdated:
                        'budget/vuedaf/projections/pluriannuelles/justif-modif',
                    statusUpdated:
                        'budget/vuedaf/projections/pluriannuelles/statut-modif',
                    pinnedPanelToggled:
                        'budget/vuedaf/projections/pluriannuelles/toggle-volet-saisie',
                    groupingChanged:
                        'budget/vuedaf/projections/pluriannuelles/modif-ordre-groupement',
                    export: 'budget/vuedaf/projections/pluriannuelles/export',
                    compactModeToggled:
                        'budget/vuedaf/projections/pluriannuelles/compact-mode',
                },
            },
        },
        budgetProposal: {
            pageOpened: 'budget/budgetproposal/consultation-page',
            viewByResourceTabClicked:
                'budget/budgetproposal/clic-onglet-ressources',
            viewByOperationTabClicked:
                'budget/budgetproposal/clic-onglet-opérations',
            operationClicked: 'budget/budgetproposal/clic-opération',
            draftOperation: {
                create: 'budget/budget/budgetproposal/creer-operation-manty',
                confirmCreation:
                    'budget/budgetproposal/valider-creation-operation-manty',
            },
            filterBudgetLineCode:
                'budget/budgetproposal/filtre-catégorie-budgétaire',
            expenseIncome: {
                operations: {
                    gridGrouping:
                        'budget/budgetproposal/depensesrecettes/opérations/groupement-tableau',
                    gridSearch:
                        'budget/budgetproposal/depensesrecettes/opérations/recherche-tableau',
                    gridSort:
                        'budget/budgetproposal/depensesrecettes/opérations/tri-tableau',
                    toggleEmpty:
                        'budget/budgetproposal/depensesrecettes/opérations/click-toggle-opérations-non-utilisées',
                },
                imputations: {
                    gridGrouping:
                        'budget/budgetproposal/depensesrecettes/imputations/groupement-tableau',
                    gridUnpack:
                        'budget/budgetproposal/depensesrecettes/imputations/dépliage-inscriptions',
                    gridSearch:
                        'budget/budgetproposal/depensesrecettes/imputations/recherche-tableau',
                    labelSearch:
                        'budget/budgetproposal/depensesrecettes/imputations/recherche-libellé',
                    gridSort:
                        'budget/budgetproposal/depensesrecettes/imputations/tri-tableau',
                    toggleEmpty:
                        'budget/budgetproposal/depensesrecettes/imputations/click-toggle-imputations-non-utilisées',
                    exportCSV:
                        'budget/budgetproposal/depensesrecettes/click-export-CSV',
                    exportExcel:
                        'budget/budgetproposal/depensesrecettes/click-export-excel',
                    addOrphanInscription:
                        'budget/budgetproposal/depensesrecettes/ajout-orpheline',
                    addOrphanOperation:
                        'budget/budgetproposal/depensesrecettes/ajout-operation-orpheline',
                    addOrphanChapter:
                        'budget/budgetproposal/depensesrecettes/ajout-chapitre-orpheline',
                    addOrphanNature:
                        'budget/budgetproposal/depensesrecettes/ajout-nature-orpelhine',
                    orphanPreAssignment:
                        'budget//*/budget_proposal/*/resource/*/*/imputations/preassign-orphan-inscription',
                    classifyOrphan:
                        'budget/budgetproposal/depensesrecettes/classer-orpheline',
                },
                chartBySelectedDim:
                    'budget/budgetproposal/depensesrecettes/clic-dropdown-graph-propositions-budgétaires-par',
                operationsTabClicked:
                    'budget/budgetproposal/depensesrecettes/click-vue-opérations',
                imputationsTabClicked:
                    'budget/budgetproposal/depensesrecettes/click-vue-imputations',
                filterBudgetLineCode:
                    'budget/budgetproposal/depensesrecettes/filtre-catégorie-budgétaire',
            },
            analyse: {
                pageOpened: 'budget/budgetproposal/analyse/consultation-page',
                argumentSave:
                    'budget/budgetproposal/analyse/enregistrement-argumentaire',
                pivotGridExport: 'budget/budgetproposal/analyse/export-tcd',
                penModalOpened:
                    'budget/budgetproposal/analyse/modalestylo/ouverture-modale',
                saveFilter:
                    'budget//*/budget_proposal/*/resource/*/analyze/edit-filter',
            },
            allArguments: {
                create: 'budget/budgetproposal/argumentaires/creation',
                rename: 'budget/budgetproposal/argumentaires/renommage',
                delete: 'budget/budgetproposal/argumentaires/suppression',
                duplicate: 'budget/budgetpurposal/argumentaires/duplication',
            },
            argument: {
                pageOpened:
                    'budget/budgetproposal/argumentaire/consultation-page',
                layoutChange: 'budget/budgetproposal/argumentaire/mise-en-page',
                backButton: 'budget/budgetproposal/argumentaire/click-retour',
                share: 'budget/budgetproposal/argumentaire/click-partage',
                pdfExport: 'budget/budgetproposal/argumentaire/export-pdf',
                pivotGridExport:
                    'budget/budgetproposal/argumentaire/export-tcd',
                penModalOpened:
                    'budget/budgetproposal/argumentaire/modalestylo/ouverture-modale',
            },
            projection: {
                atterrissage: {
                    pageOpened:
                        'budget/budgetproposal/projections/atterrissage/consultation-page',
                    statusUpdated:
                        'budget/budgetproposal/projections/atterrissage/statut-modif',
                    amountUpdated:
                        'budget/budgetproposal/projections/atterrissage/montant-modif',
                    commentUpdated:
                        'budget/budgetproposal/projections/atterrissage/justif-modif',
                    groupingChanged:
                        'budget/budgetproposal/projections/atterrissage/modif-ordre-groupement',
                    filterBudgetLineCode:
                        'budget/budgetproposal/projections/filtre-catégorie-budgétaire',
                },
                pluriannual: {
                    pageOpened:
                        'budget/budgetproposal/projections/pluriannuelles/consultation-page',
                    statusUpdated:
                        'budget/budgetproposal/projections/pluriannuelles/statut-modif',
                    amountUpdated:
                        'budget/budgetproposal/projections/pluriannuelles/montant-modif',
                    pinnedPanelToggled:
                        'budget/budgetproposal/projections/pluriannuelles/toggle-volet-saisie',
                    groupingChanged:
                        'budget/budgetproposal/projections/pluriannuelles/modif-ordre-groupement',
                    detailsClicked:
                        'budget/budgetproposal/projections/pluriannuelles/clic-detail',
                    filtered: '',
                    sectionToggled:
                        'budget/budgetproposal/projections/pluriannuelles/toggle-section',
                    resourceToggled:
                        'budget/budgetproposal/projections/pluriannuelles/toggle-ressource',
                    commentUpdated: '',
                    export: '',
                },
            },
        },
        general: {
            labelSearch: 'budget/general/inscriptions/recherche-libellé',
            gridGrouping: 'budget/general/inscriptions/groupement-tableau',
            addCategory: 'budget/general/inscriptions/ajout-catégorie',
            associateCategory:
                'budget/general/inscriptions/attribution-catégorie',
            detailsOpened: 'budget/general/inscriptions/consultation-détail',
            addComment:
                'budget/general/inscriptions/ajout-commentaire-historique',
            addInscription: 'budget/general/inscriptions/ajout-inscription',
            addOrphanInscriptionToggle:
                'budget/general/inscriptions/ajout-orpheline-toggle',
            editInscription: 'budget/general/inscriptions/édition-inscription',
            moveInscription:
                'budget/general/inscriptions/déplacement-inscription',
            deleteInscription:
                'budget/general/inscriptions/suppression-inscription',
            changeStatusBatch:
                'budget/general/inscriptions/changement-statuts-batch',
            addInscriptionCommentMention:
                'budget/general/inscriptions/historique/ajout-tag',
            operation360: {
                editDescription:
                    'budget/general/operation360/edition-paragraphe',
                backButtonClick: 'budget/general/operation360/click-retour',
                addSemiOrphanInscription:
                    'budget/general/operation360/ajout-semi-orpheline',
                addInscription: 'budget/general/operation360/ajout-inscription',
                clickMentionCharTool:
                    'budget/general/operation360/paragraphe/clic-tool-tag',
                addDescriptionMention:
                    'budget/general/operation360/paragraphe/ajout-tag',
            },
            fullScreen: {
                open: 'budget//*/budget_proposal/*/resource/*/*/imputations/click-open-fullscreen',
                close: 'budget//*/budget_proposal/*/resource/*/*/imputations/click-close-fullscreen',
            },
            keyDates: 'budget/display-key-dates',
            groupedActions: {
                openStatusesModal:
                    'budget/general/inscriptions/click-change-status-batch',
                confirmBatchStatuses:
                    'budget/general/inscriptions/confirm-change-status-batch',
                openProjectsModal:
                    'budget/general/inscriptions/click-edit-category-batch',
                confirmBatchProjects:
                    'budget/general/inscriptions/confirm-edit-category-batch',
                openRateModal:
                    'budget/general/inscriptions/click-apply-percentage-batch',
                confirmBatchRate:
                    'budget/general/inscriptions/confirm-apply-percentage-batch',
                openCommentsModal:
                    'budget/general/inscriptions/click-comment-inscriptions-batch',
                confirmBatchComments:
                    'budget/general/inscriptions/confirm-comment-inscriptions-batch',
                openDeleteModal:
                    'budget/general/inscriptions/click-delete-inscriptions-batch',
                confirmBatchDelete:
                    'budget/general/inscriptions/confirm-delete-inscriptions-batch',
            },
            settingsManagement: {
                setAsDefault: 'budget//**/table_config/set-as-default',
                openTableConfig: 'budget//**/table_config',
                resetDisplay: 'budget//**/table_config/reset-display',
                copyDisplay: 'budget//**/table_config/copy-display',
            },
        },
        settings: {
            addBudget: 'budget//*/admin/budget/manage_budget/click-add-budget',
            confirmAddBudget:
                'budget//*/admin/budget/manage_budget/confirm-add-budget',
            confirmResource:
                'budget//*/admin/budget/manage_budget/confirm-allow-budget-edit',
            changeResourceAccessType:
                'budget//*/admin/budget/manage_budget/select-allow-edit-option',
            confirmAtterrissageVentilation:
                'budget//*/admin/budget/manage_budget/confirm-atterrissage-axes',
            confirmPluriannualProjectionVentilation:
                'budget//*/admin/budget/manage_budget/confirm-pluriannual-projections-axes',
            confirmDuplicateSettings:
                'budget//*/admin/budget/manage_budget/confirm-duplicate-settings',
            initializeInscriptions:
                'budget//*/budget_admin/manage_budget/*/click-initialize',
            confirmInitializeInscriptions:
                'budget//*/budget_admin/manage_budget/*/confirm-initialize',
            duplicateSettings:
                'budget//*/admin/budget/manage_budget/click-duplicate-settings',
            manageArguments: {
                openModal:
                    'budget//*/budget_admin/manage_budget/gestionnaire.name/click-manage-button//*',
                confirmBulkDuplication:
                    'budget//*/budget_admin/manage_budget/gestionnaire.name/confirm-bulk-duplication',
            },
            duplicateArgumentaire: {
                click: 'settings/budget/duplicate-argu/click',
                confirmDuplication:
                    'settings/budget/duplicate-argu/confirm-duplication',
                budget: 'settings/budget/duplicate-argu/toggle-budget',
                section: 'settings/budget/duplicate-argu/toggle-section',
                resource: 'settings/budget/duplicate-argu/toggle-ressource',
            },
            projects: {
                search: 'budget/réglages/catégories/recherche',
                sort: 'budget/réglages/catégories/tri-tableau-catégorie',
                add: 'budget/réglages/catégories/clic-ajouter',
                confirmAdd: 'budget/réglages/catégories/confirmation-ajout',
                delete: 'budget/réglages/catégories/clic-supprimer',
                confirmDelete:
                    'budget/réglages/catégories/confirmation-suppression',
                patch: 'budget/réglages/catégories/clic-renommer',
                confirmPatch:
                    'budget/réglages/catégories/confirmation-renommage',
            },
            manageProjects: {
                clickCategoryRequired:
                    'budget//*/budget_admin/manage_projects/click-category-required',
                confirmCategoryRequired:
                    'budget//*/budget_admin/manage_projects/confirm-category-required',
                clickAddGroup:
                    'budget//*/budget_admin/manage_projects/click-add-group',
                confirmAddGroup:
                    'budget//*/budget_admin/manage_projects/confirm-add-group',
                clickEditGroup:
                    'budget//*/budget_admin/manage_projects/click-edit-group',
                confirmEditGroup:
                    'budget//*/budget_admin/manage_projects/confirm-edit-group',
                clickDeleteGroup:
                    'budget//*/budget_admin/manage_projects/click-delete-group',
                confirmDeleteGroup:
                    'budget//*/budget_admin/manage_projects/confirm-delete-group',
                confirmReassignmentCategory:
                    'budget//*/budget_admin/manage_projects/confirm-reassignment-category',
                selectGroupOnCategory:
                    'budget//*/budget_admin/manage_projects/select-group-on-category',
            },
            toggleAddReported: 'budget/toggle-add-reported',
            cadrage: {
                exercice: '//*/budget_admin/manage_cadrage/select-year',
                metric: '//*/budget_admin/manage_cadrage/select-cadrage-base',
                custom: {
                    add: 'budget//*/budget_admin/manage_cadrage/confirm-add-custom-cadrage',
                    edit: 'budget//*/budget_admin/manage_cadrage/confirm-edit-custom-cadrage',
                    delete: 'budget//*/budget_admin/manage_cadrage/confirm-delete-custom-cadrage',
                },
                byChapter:
                    'budget//*/budget_admin/manage_cadrage/click-detail-chapitre',
                byChapterGraph:
                    'budget//*/budgetproposal/depensesrecettes/click-graph-cadrage-chapitre',
                gaugeDetail:
                    'budget//*/budget_proposal/**/cadrage-gauge-detail',
                evolutionRate: {
                    openModal:
                        'budget//*/budget_admin/manage_cadrage/click-apply-percentage-button',
                    applyRate:
                        'budget//*/budget_admin/manage_cadrage/confirm-apply-percentage',
                },
            },
        },
        ux: {
            breadcrumbsClick: 'budget/ux/click-fil-darianne',
            homeBudgetClick: 'budget/ux/click-icone-budget',
            vueDafClick: 'budget/ux/click-bouton-vue-daf',
            homeFiltersChanged: 'budget/ux/changement-filtres-accueil',
            settingsBudgetClick: 'budget/ux/clic-réglages-budget',
            keyDatesWidgetClick: 'budget/ux/clic-widget-dates-clés',
        },
        profile: {
            changeEmailNotificationsFrequency:
                'budget/profile/change-email-notifications-frequency',
        },
    },
    projections: {
        projectionsOverview: {
            pageOpened: 'projections/pluriannuelles/consultation-page',
            filtered: 'projections/pluriannuelles/modif-filtre',
            sectionToggled: 'projections/pluriannuelles/toggle-section',
            amountUpdated: 'projections/pluriannuelles/montant-modif',
            commentUpdated: 'projections/pluriannuelles/justif-modif',
            statusUpdated: 'projections/pluriannuelles/statut-modif',
            pinnedPanelToggled:
                'projections/pluriannuelles/toggle-volet-saisie',
            groupingChanged:
                'projections/pluriannuelles/modif-ordre-groupement',
            export: 'projections/pluriannuelles/export',
            compactModeToggled: 'projections/pluriannuelles/compact-mode',
            detailsClicked: 'projections/pluriannuelles/clic-detail',
            resourceToggled: 'projections/pluriannuelles/toggle-ressource',
        },
    },
    human_resources: {
        fullScreen: {
            open: 'prh//*/human_resources/click-open-fullscreen',
            close: 'prh//*/human_resources/click-close-fullscreen',
        },
        dataTreeView: 'prh//*/human_ressources/select-arborescent-view',
        simulation: {
            create: 'prh/simulation_home/simulation_create',
            exportAccountablePdf: 'prh/simulations/result_accounting_export',
            accountableDisplay:
                'prh/simulations/result_accounting_view_display',
            duplicate: 'prh/simulation_home/simulation_duplicate',
            duplicatedHypothesisSelect:
                'prh//*/human_resources/simulation_home/duplicate/duplicated-hypothesis-select',
            suggestedAgentDepartureHypothesisSelect:
                'prh//*/human_resources/simulation_home/SIRH-hypothesis-select',
            suggestedAgentDepartureHypothesisSelectAll:
                'prh//*/human_resources/simulation_home/SIRH-hypothesis-select-all',
            scenario: {
                updateHypotheses: 'prh/simulation/scenario/new_values_apply',
                createEmployerCostRateHypothesis:
                    'prh//*/human_resources/simulation/*/scenario/employer-cost/confirm-new',
                updateEmployerCostRateHypothesis:
                    'prh//*/human_resources/simulation/*/scenario/employer-cost/confirm-edit',
                smicDetail: 'prh/simulation/scenario/view_details_SMIC',
                createRegimeIndemnitaireMensuelHypotheses:
                    'prh/simulation/scenario/new_hypothesis_validate',
            },
            recurrent: {
                createAgentDepartureHypothesis:
                    'prh/simulation/recurrent_departure_hypothesis_validate',
                dataRefresh: 'prh/simulation/recurrent_data_refresh',
                addAgentFromVacantPositionModal:
                    'prh//*/human_resources/simulation/*/recurrent/click-add-agent-from-vacant-position',
                vacantPositionModalOpened:
                    'prh//*/human_resources/simulation/*/recurrent/click-vacant-positions-list',
                createAgentArrivalHypothesis:
                    'prh//*/human_resources/simulation/*/recurrent/validate-add-agent',
                validateEnvelopeHypothesis:
                    'prh//*/human_resources/simulation/*/recurrent/validate-envelope',
                validateAgentPonctualPrimeHypothesis:
                    'prh/simulation/recurrent_one_time_bonus_hypothesis_validate',
                createRimHypothesis:
                    'prh/simulation/recurrent_monthly_premium_hypothesis_validate',
                createGradeAdvancementHypothesis:
                    'prh/simulation/recurrent_promotion_hypothesis_validate',
            },
            nonRecurrent: {
                createAgentArrivalHypothesis:
                    'prh//*/human_resources/simulation/*/non_recurrent/validate-add-agent',
                applyHypothesesConfig:
                    'prh/simulation/non_recurrent_statut_hypothesis_setup_apply',
                addAgentFromVacantPositionModal:
                    'prh//*/human_resources/simulation/*/non_recurrent/click-add-agent-from-vacant-position',
                vacantPositionModalOpened:
                    'prh//*/human_resources/simulation/*/non_recurrent/click-vacant-positions-list',
                createAgentDepartureHypothesis:
                    'prh/simulation/non_recurrent_departure_hypothesis_agent_validate',
                validateEnvelopeHypothesis:
                    'prh//*/human_resources/simulation/*/non_recurrent/validate-envelope',
                agentDataRefresh:
                    'prh/simulation/non_recurrent_hypothesis_agent_data_refresh',
                envelopeDataRefresh:
                    'prh/simulation/non_recurrent_hypothesis_envelope_data_refresh',
                validateAgentPonctualPrimeHypothesis:
                    'prh/simulation/non_recurrent_one_time_bonus_hypothesis_agent_validate',
            },
            payComponents: {
                validatePayComponentEnvelopeHypothesis:
                    'prh//*/human_resources/simulation/*/pay_components/validate-envelope',
                dataRefresh:
                    'prh/simulation/components_hypothesis_data_refresh',
            },
            envelope: {
                validateGlobalAmount:
                    'prh//*/human_resources/simulation/**/envelope-confirm-global-amount',
            },
            elu: {
                dataRefresh: 'prh/simulation/elu_data_refresh',
                addEluEnvelope:
                    'prh//*/human_ressources/simulation/*/elu/click-add-elu-envelope',
                addHorsFluxEnvelope:
                    'prh//*/human_ressources/simulation/*/elu/click-add-hors-flux-envelope',
                resultToggle:
                    'prh//*/human_ressources/simulation/*/result/select-expenses-scope',
            },
            hypothesesSummary: {
                openPage: 'prh/simulation/simulation_resume_display',
            },
            results: {
                openPage: 'prh/simulation/simulation_result_display',
                votedAmountEdit:
                    'prh//*/human_resources/simulation/results/click_add_voted_amount',
                votedAmountSave:
                    'prh//*/human_resources/simulation/results/confirm_add_voted_amount',
                gaugeItemChange:
                    'prh//*/human_resources/simulation/results/select_gauge_metrics',
            },
        },
        retrospective: {
            create: 'prh/retrospective_home/retrospective_create',
        },
        analyseMasseSalariale: {
            create: 'prh/human_ressources/analyse_masse_salariale_home_create',
        },
        settings: {
            revertBatchEvent:
                'prh//*/human_ressources/settings/revert-modification-batch ',
            revertEvent: 'prh//*/human_ressources/settings/revert-modification',
            eventsModalOpened:
                'prh//*/human_ressources/settings/click-settings-history',
            editRecurrencePositionMapping:
                'prh//*/human_resources/settings/agentMapping/edit-recurrence',
            editIndiciairePositionMapping:
                'prh//*/human_resources/settings/agentMapping/edit-indiciaire',
            editMantyStatusPositionMapping:
                'prh//*/human_resources/settings/agentMapping/edit-status',
            editRetirementSystemPositionMapping:
                'prh//*/human_resources/settings/agentMapping/edit-retirement-system',
            editValidateFieldPositionMapping:
                'prh//*/human_resources/settings/change-setting-status',
            selectEmployerCostRate:
                'prh//*/human_resources/settings/DefaultEmployerCostMapping/confirm-edit',
            selectPayComponent:
                'prh//*/human_resources/settings/wageTypeMapping/select-pay-component',
            selectWageType:
                'prh//*/mappings?category=wage_type.pay_component/select-pay-component',
        },
    },
    decision: {
        explore: {
            filterGroups: {
                add: 'decision//*/explore/add_filter_group',
                split: 'decision//*/explore/split_filter_group',
                switchOperatorFocus:
                    'decision//*/explore/switch_filter_operator_focus',
                switchOperator: 'decision//*/explore/switch_filter_operator',
            },
        },
    },
    global: {
        ux: {
            notificationsCenter: {
                clickIcon: 'global/ux/clic-centre-notifications',
                markAllAsRead:
                    'global/ux/centre-notifications/clic-tout-marquer-comme-lu',
                clickNotification:
                    'global/ux/centre-notifications/clic-notification',
            },
        },
        profile: {
            toggleEmailNotifications:
                'global/profile/toggle-email-notifications',
        },
    },
};
export const TRACKABLE_EVENTS_METADATA = {
    human_resources: {
        fullScreen: {
            generalDataGrid:
                'prh//*/human_resources/simulation/*/scenario/scenarioArray',
            recurrentGrid:
                'prh//*/human_resources/simulation/*/recurrent/agentGrid',
            nonRecurrentEnvelopeGrid:
                'prh//*/human_resources/simulation/*/non_recurrent/envelopeGrid',
            nonRecurrentAgentGrid:
                'prh//*/human_resources/simulation/*/non_recurrent/agentGrid',
            payComponentGrid:
                'prh//*/human_resources/simulation/*/pay_components/payComponents',
            eluGrid: 'prh//*/human_resources/simulation/*/elu/eluGrid',
            horsFluxGrid:
                'prh//*/human_resources/simulation/*/hors_flux/horsFluxGrid',
            resultNatureGrid:
                'prh//*/human_resources/simulation/*/result/nature',
            resultGrid:
                'prh//*/human_resources/simulation/*/result/time/resultsBy',
            permanentSalaryMassGrid:
                'prh//*/human_resources/analyse_masse_salariale/*/diary/permanentSalaryMass',
            nonPermanentSalaryMassGrid:
                'prh//*/human_resources/analyse_masse_salariale/*/diary/nonPermanentSalaryMass',
            supCompGrid:
                'prh//*/human_resources/analyse_masse_salariale/*/diary/hsHcAmounts',
            payComponentPermanentGrid:
                'prh//*/human_resources/analyse_masse_salariale/*/diary/permanentSalaryMassByPayComponents',
            payComponentNonPermanentGrid:
                'prh//*/human_resources/analyse_masse_salariale/*/diary/nonPermanentSalaryMassByPayComponents',
            echelonGrid:
                'prh//*/human_resources/analyse_masse_salariale/*/diary/echelon',
            absenceGrid:
                'prh//*/human_resources/analyse_masse_salariale/*/occupations/absenceCost',
            vacantJobGrid:
                'prh//*/human_resources/analyse_masse_salariale/*/occupations/vacant',
            hrPositionMappingGrid:
                'prh//*/human_resources/settings/hrPositionMapping',
            hrDefaultEmployerCostMappingGrid:
                'prh//*/human_resources/settings/hrDefaultEmployerCostMapping',
            gradeMappingGrid: 'prh//*/human_resources/settings/gradeMapping',
            mappingsOrgChartHrGrid:
                'prh//*/human_resources/settings/mappingsOrgChartHr',
            payComponentMappingsGrid:
                'prh//*/human_resources/settings/payComponentMapping',
        },
    },
};
